/**
 * FormToggle
 * ----------
 * Thin RHF wrapper around the app's custom ToggleSwitch component, for
 * boolean fields whose established UI is a toggle switch rather than a
 * Polaris Checkbox (ToggleSwitch has no direct Polaris/FormFields
 * equivalent). Mirrors FormCheckbox's override/AB-disable behavior.
 */

import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import { useController, useFormContext, type FieldValues, type Path } from "react-hook-form";
import { useVariantFieldDisabled } from "./context/VariantFieldContext";

export interface FormToggleProps<TFieldValues extends FieldValues = FieldValues> {
    name: Path<TFieldValues> | (string & NonNullable<unknown>);
    checked?: boolean;
    onChange?: (val: boolean, field: ReturnType<typeof useController<TFieldValues>>["field"]) => void;
    disabled?: boolean;
    zoom?: number;
}

export function FormToggle<TFieldValues extends FieldValues = FieldValues>({
    name,
    checked,
    onChange,
    disabled,
    zoom = 1,
}: FormToggleProps<TFieldValues>) {
    const { control } = useFormContext<TFieldValues>();
    const { field } = useController<TFieldValues>({ control, name: name as any });
    const isAbDisabled = useVariantFieldDisabled(name as string);

    const isChecked = checked !== undefined ? checked : Boolean(field.value);
    const handleToggle = () => {
        const next = !isChecked;
        if (onChange) onChange(next, field);
        else field.onChange(next);
    };

    return (
        <ToggleSwitch
            checked={isChecked}
            setToggle={handleToggle}
            disabled={!!(disabled || isAbDisabled)}
            zoom={zoom}
        />
    );
}
